import { SessionStatus } from "globals/enums";
import * as Sentry from "@sentry/react";

export const PROCTORING_STOPPED_CODE = "PROCTORING_STOPPED" as const;
export const PROVIEW_ROOT_REMOVED_REASON = "PROVIEW_ROOT_REMOVED" as const;

export interface ProctoringStoppedError {
  code: typeof PROCTORING_STOPPED_CODE;
  reason: typeof PROVIEW_ROOT_REMOVED_REASON;
}

interface MonitorOptions {
  root: HTMLElement;
  getIsMonitoring: () => boolean;
  getIsExpectedRemoval: () => boolean;
  onError: (error: ProctoringStoppedError) => void;
  target?: Node;
}

/**
 * Watches the stable document tree rather than the Proview root itself. The
 * root can be removed by the host while this runtime is still alive, so an
 * observer attached to the root would disappear with it.
 */
export const observeProviewRoot = ({
  root,
  getIsMonitoring,
  getIsExpectedRemoval,
  onError,
  target
}: MonitorOptions): (() => void) => {
  let errorReported = false;
  const observerTarget = target ?? root.parentNode ?? document.body;

  const observer = new MutationObserver(() => {
    // Let a synchronous move/reparent operation settle before checking the
    // root. MutationObserver callbacks run as microtasks, so another
    // microtask is still too early for this purpose.
    setTimeout(reportIfUnexpectedlyRemoved, 0);
  });

  const reportIfUnexpectedlyRemoved = (): void => {
    if (
      errorReported ||
      root.isConnected ||
      !getIsMonitoring() ||
      getIsExpectedRemoval()
    ) {
      return;
    }

    errorReported = true;
    observer.disconnect();
    Sentry.addBreadcrumb({
      category: "proctoring",
      message: "Proview root was unexpectedly removed",
      level: "error",
      data: { code: PROCTORING_STOPPED_CODE, reason: PROVIEW_ROOT_REMOVED_REASON }
    });
    onError({
      code: PROCTORING_STOPPED_CODE,
      reason: PROVIEW_ROOT_REMOVED_REASON
    });
  };

  observer.observe(observerTarget, { childList: true });

  return () => observer.disconnect();
};

export const isActiveProctoringSession = (state: {
  sessionId: string | number;
  sessionStatus: SessionStatus | string;
}): boolean =>
  Boolean(state.sessionId) &&
  state.sessionStatus !== SessionStatus.Completed &&
  state.sessionStatus !== SessionStatus.Terminated;
